import type { DoczFragment, DoczModule } from "@norkron/web-docz";
import {
  type CanvasSurfaceHandle,
  createFragmentSurface,
  type FragmentSurface,
  type HighlightRect,
  type PageLayoutModule,
  type PageRect,
} from "@norkron/web-page-layout";
import type { MentionRef } from "../../api/types";
import {
  createProbeResolver,
  FRAGMENT_PAPER,
  surfaceInk,
  withAlpha,
} from "../../lib/canvasCommentColors";
import { trackFocusSource } from "../../lib/pointerFocus";
import { applyCanvasScale } from "../canvas/canvasPresentation";
import {
  commentActivatableRanges,
  type CommentTarget,
  commentBlockFromFragment,
  commentInteractiveTargets,
  commentPageConfig,
  blockWithDefaultInk,
  targetAtPoint,
} from "./commentSurfaceModel";

/**
 * `commentCanvasSurface` — the IMPERATIVE half of the canvas comment body: one
 * comment fragment mounted on the document engine, with its links and mention
 * chips painted and hit-testable.
 *
 * It is DOM-only and React-free on purpose. `<CommentCanvasBody>` owns the
 * lifecycle questions (is the card on screen, is there a surface budget, did the
 * engine load, what does the accessible mirror say); this owns the surface. Split
 * that way, the whole interactive contract — measure a band, tint it, resolve a
 * pointer to a target — is reachable without a React tree, and the component's
 * mount effect stays short enough to read.
 *
 * RULE ZERO: the mount is `createFragmentSurface`'s — the SAME five-step fragment
 * plumbing the anchored quote uses (structured docz handle → `DoczEditBridge` →
 * `createWasmRelayout` with a live page-config getter → `createCanvasSurface` at
 * `interactivity: 'read'`), which this file and `quoteSurface.ts` had each spelled
 * out. The drag-select, double-click word select, ⌘A and the IR-sliced copy are
 * the surface's; the block projection and the link byte ranges are web-docz's; the
 * colour policy is `lib/canvasCommentColors`; the scale is elaborate's ONE
 * presentation policy, INJECTED rather than decided by the library. What is here
 * is only the join.
 *
 * ## The KEYBOARD route, and what it does and does not close
 *
 * The painted targets were pointer-only: hover, `cursor: pointer`, a click. A
 * sighted keyboard-only user had no route to a link inside a comment at all —
 * **WCAG 2.1.1 (Keyboard, A)** unmet, **2.4.7 (Focus Visible, AA)** with it. The
 * bands are now also declared as the surface's `activatable` set, so `Tab` walks
 * them in document order with a painted focus ring and `Enter`/`Space` activates,
 * through the SAME `onActivate` the pointer uses — one activation path, so the two
 * routes cannot diverge.
 *
 * Pointer activation deliberately does NOT move the ring: a mouse click leaving a
 * focus indicator behind is the `:focus-visible` mistake, and the ring is the
 * keyboard's report of where it is.
 *
 * ⚠️ RESIDUAL, stated rather than hidden. `<CommentCanvasBody>` mounts the
 * accessible mirror INSIDE this surface's host, and a mirror under
 * `MIRROR_ROOT_STYLE` is CLIPPED, not hidden — its real `<a>` elements are
 * therefore already invisible tab stops today (a pre-existing 2.4.7 failure). With
 * the bands in place, each link is reachable VISIBLY first and invisibly second,
 * i.e. a duplicate tab stop (2.4.3). Closing it means taking a clipped mirror's
 * interactive descendants out of sequential focus, which is a `DocumentMirror`
 * decision in `@norkron/web-docz` and is not taken from here.
 */

/** A mounted comment surface. */
export interface CommentSurfaceMount {
  readonly surface: CanvasSurfaceHandle;
  /** Re-measure every interactive band and repaint. Byte offsets are stable
   *  across relayout; PIXELS ARE NOT, so this is a full pass whenever the layout
   *  can have moved (a card resize), never a cache. */
  remeasure(): void;
  dispose(): void;
}

export interface CommentSurfaceInput {
  /** The node the engine paints into. Its `clientWidth` IS the column measure. */
  readonly host: HTMLElement;
  /**
   * Where the theme probe is appended. Deliberately NOT `host`:
   * `createCanvasSurface` calls `replaceChildren()` on its container and the tile
   * painter owns its children, so a probe left there is at the mercy of a repaint.
   */
  readonly themeHost: HTMLElement;
  readonly docz: DoczModule;
  readonly engine: PageLayoutModule;
  readonly fragment: DoczFragment;
  readonly mentions: readonly MentionRef[];
  /** Stable id for the single block — the comment's own identity. */
  readonly blockId: string;
  readonly dpr: number;
  /** A link or a mention chip was activated by a pointer. */
  readonly onActivate: (target: CommentTarget) => void;
}

/** The four tints the comment surface paints in, from the LIVE theme tokens (so
 *  a mid-session theme switch is picked up on the next mount). */
function palette(themeHost: HTMLElement): {
  rest: string;
  glow: string;
  selection: string;
  ring: string;
} {
  // `--norkron-focus-ring-color` FIRST — the product's one focus token (it
  // resolves to the accent, so the value is unchanged; the NAME is the point:
  // the CSS ring on the host and this painted in-canvas ring must move together
  // or the same surface gets two different indicators).
  const resolve = createProbeResolver(themeHost);
  const accent =
    resolve("--norkron-focus-ring-color") || resolve("--norkron-accent");
  return {
    rest: withAlpha(accent, 0.14) ?? "rgba(37, 99, 235, 0.14)",
    glow: withAlpha(accent, 0.3) ?? "rgba(37, 99, 235, 0.30)",
    selection: withAlpha(accent, 0.3) ?? "rgba(37, 99, 235, 0.30)",
    // OPAQUE, unlike the three washes above. A focus indicator has to be legible
    // against paper, against a resting link tint and against a live selection tint
    // at once, and a translucent ring over a translucent wash is precisely the case
    // where it stops being visible.
    ring: withAlpha(accent, 1) ?? "rgb(37, 99, 235)",
  };
}

/**
 * The fragment as a one-block docz document laid out to the card.
 *
 * `mirror: false` — `<CommentCanvasBody>` mounts the accessible projection itself,
 * and it MUST: a comment body's HTML goes through `sanitizeCommentHtml` FIRST
 * (`COMMENT_PROFILE`), which is narrower than the mirror's own `MIRROR_PROFILE` in
 * exactly one dimension — relative hrefs, which a document's table-of-contents
 * entry needs and a comment body must not have. Letting the fragment mount build
 * the mirror from the raw handle would widen a live control.
 *
 * No `scroller` is passed, so the surface auto-detects exactly as it always has.
 * (The quote card passes `null` because a rail of 40 quote boxes would otherwise
 * install 40 capture-phase scroll listeners; that call was made there and is not
 * silently changed here.)
 */
function buildSurface(
  input: CommentSurfaceInput,
  activatable: readonly ReturnType<typeof commentActivatableRanges>[number][],
  onActivateRange: (key: string) => void,
): FragmentSurface {
  const { host, docz, engine, fragment, blockId, dpr } = input;
  const colors = palette(input.themeHost);
  // ⛔ NO SHEET. A comment body is NOT a page — it is prose set on the reviewer's
  // own card — so it paints on NOTHING and the card's own CSS shows through
  // (`FRAGMENT_PAPER`, owner order 2026-07-30). The engine `clearRect`s the band
  // before the paper fill, so `'transparent'` leaves the blank pixels at alpha 0
  // rather than at whatever a recycled tile held last.
  //
  // ⛔ The INK still moves: the engine's default is black, which on a transparent
  // canvas over a dark card is invisible. `null` means the cascade could not be
  // read (a detached host, a test DOM) and then no ink is passed — degrading to
  // "no opinion" is safe, degrading to a guessed colour is not (`fillStyle` keeps
  // its previous value on an unpaintable string).
  const ink = surfaceInk(host);
  const block = commentBlockFromFragment(fragment, "c0");
  const built = createFragmentSurface({
    host,
    docz,
    engine,
    blocks: [ink === null ? block : blockWithDefaultInk(block, ink)],
    paper: FRAGMENT_PAPER,
    documentId: blockId,
    ...(fragment.baseVersionId != null
      ? { baseVersionId: fragment.baseVersionId }
      : {}),
    // A GETTER: re-read every frame, so a rail resize reflows the comment on the
    // next `refresh()` with no remount and no lost selection.
    pageConfig: () => commentPageConfig(host.clientWidth, 1),
    zoom: 1,
    dpr,
    maxDpr: 2,
    colors: {
      // No caret exists on a read surface (the surface does not even create the
      // caret canvas), so this is inert — stated rather than left to a default
      // that would imply a caret is coming.
      caret: "transparent",
      selection: colors.selection,
      highlight: colors.rest,
      // The KEYBOARD focus ring. Concrete, never a token: `fillStyle` cannot read
      // `var()` and SILENTLY keeps its previous value, so a ring given a token
      // paints the last layer's colour — a visible indicator in the wrong hue,
      // which is worse than none because nobody files it.
      focusRing: colors.ring,
    },
    activatable,
    onActivateRange: (range) => onActivateRange(range.key),
    // `<CommentCanvasBody>` owns the mirror — see the note above.
    mirror: false,
    // THE SCALE — through the ONE app-wide policy every canvas document surface
    // shares, never decided here. See `canvasPresentation`.
    applyScale: (h, surf, zoom) => applyCanvasScale(h, surf, zoom),
  });
  if (!built) throw new Error("commentCanvasSurface: the fragment has no blocks");
  return built;
}

/**
 * Mount one comment fragment on the document engine and wire its interactive
 * affordances. Throws whatever the engine throws — the caller decides what a
 * failure looks like to a reader.
 */
export function mountCommentSurface(
  input: CommentSurfaceInput,
): CommentSurfaceMount {
  const { host, mentions, fragment } = input;
  const colors = palette(input.themeHost);
  const targets = commentInteractiveTargets(fragment, mentions);
  /** Key → target, so the surface's keyboard activation lands on the SAME
   *  `onActivate` the pointer does. One activation path; two routes to it. */
  const byKey = new Map(targets.map((t) => [t.key, t] as const));
  const fragmentSurface = buildSurface(
    input,
    commentActivatableRanges(targets),
    (key) => {
      const target = byKey.get(key);
      if (target) input.onActivate(target);
    },
  );
  const surface = fragmentSurface.surface;

  let bands: ReadonlyMap<string, readonly PageRect[]> = new Map();
  /** The target under the pointer — the DOM `:hover` rule, restored. */
  let hovered: string | null = null;

  const paint = (): void => {
    const painted: HighlightRect[] = [];
    for (const t of targets) {
      const rects = bands.get(t.key);
      if (!rects) continue;
      const color = t.key === hovered ? colors.glow : colors.rest;
      for (const r of rects) painted.push({ ...r, color });
    }
    surface.setHighlights(painted);
  };

  const remeasure = (): void => {
    const next = new Map<string, readonly PageRect[]>();
    for (const t of targets) {
      const rects = surface.rangeRects(0, t.start, t.end);
      if (rects.length > 0) next.set(t.key, rects);
    }
    bands = next;
    paint();
  };

  /** Which target is under a client point — `pagePointAt` + containment against
   *  the PAINTED bands, never `positionAt`. `positionAt` is the caret hit test
   *  and it SNAPS: a press in a margin is rescued onto the nearest line, which is
   *  right for "put the caret here" and would make a click on blank paper open a
   *  link nowhere near the pointer. */
  const hit = (ev: MouseEvent): CommentTarget | null =>
    targetAtPoint(targets, bands, surface.pagePointAt(ev.clientX, ev.clientY));

  // A click that ENDED A DRAG is a select gesture, not an activate gesture, so a
  // live selection suppresses it.
  const onClick = (ev: MouseEvent): void => {
    if (surface.selection()) return;
    const target = hit(ev);
    if (!target) return;
    ev.preventDefault();
    input.onActivate(target);
  };

  // HOVER restores the `cursor: pointer` that made a DOM span look clickable in
  // the first place — without it there is no way to DISCOVER that the words above
  // are interactive — plus the brightened band. Guarded on CHANGE and repainted
  // from the CACHED bands, so moving the pointer across a comment costs one hit
  // test per event and one overlay repaint per boundary crossed, never a
  // re-measure.
  const onMove = (ev: MouseEvent): void => {
    const target = hit(ev);
    const next = target?.key ?? null;
    if (next === hovered) return;
    hovered = next;
    host.style.cursor = next === null ? "" : "pointer";
    host.title = target?.kind === "link" ? target.href : "";
    paint();
  };

  const onLeave = (): void => {
    if (hovered === null) return;
    hovered = null;
    host.style.cursor = "";
    host.title = "";
    paint();
  };

  host.addEventListener("click", onClick);
  host.addEventListener("mousemove", onMove);
  host.addEventListener("mouseleave", onLeave);
  // WHICH MODALITY FOCUSED THIS SURFACE — the stylesheet keys its ring on it. The
  // engine focuses the container from `pointerdown`, which Chromium scores as
  // `:focus-visible`, so a mouse click painted a ring nobody asked for. See
  // `lib/pointerFocus`.
  const untrackFocus = trackFocusSource(host);

  // A rail resize changes the measure, which changes the wrap, which moves every
  // band. Re-laid out through the config getter, then re-measured. Guarded on an
  // integer width change so a sub-pixel reflow is not a relayout storm.
  let lastWidth = Math.round(host.clientWidth);
  const RO = (globalThis as { ResizeObserver?: typeof ResizeObserver })
    .ResizeObserver;
  const ro = RO
    ? new RO(() => {
        const w = Math.round(host.clientWidth);
        if (w === lastWidth) return;
        lastWidth = w;
        fragmentSurface.refresh();
        remeasure();
      })
    : null;
  ro?.observe(host);

  remeasure();

  return {
    surface,
    remeasure,
    dispose(): void {
      host.removeEventListener("click", onClick);
      host.removeEventListener("mousemove", onMove);
      host.removeEventListener("mouseleave", onLeave);
      untrackFocus();
      host.style.cursor = "";
      host.title = "";
      ro?.disconnect();
      fragmentSurface.destroy();
    },
  };
}
